import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { COLORS, FONTS } from '../theme';
import { SCHOOL, NAV, IMAGES } from '../data/siteData';
import { Section, SectionHeading, Reveal, Icon, Wave } from './common';

function ContactCard({ icon, title, children, color }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', p: 2.5, borderRadius: '24px', bgcolor: '#fff', border: `3px solid ${color}`, boxShadow: `0 8px 0 ${color}44` }}>
      <Box sx={{ flexShrink: 0, width: 50, height: 50, borderRadius: '50%', bgcolor: color, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name={icon} />
      </Box>
      <Box>
        <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 800, fontSize: 19, color }}>{title}</Typography>
        <Typography component="div" sx={{ fontSize: 15.5, lineHeight: 1.6, color: COLORS.text }}>{children}</Typography>
      </Box>
    </Box>
  );
}

export function Contact() {
  return (
    <Section id="contact" bg={COLORS.cream} sx={{ pt: { xs: 8, md: 10 }, pb: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        <SectionHeading
          eyebrow="Contact Us"
          title="Come Visit Our Happy Place"
          subtitle="We would love to meet you and your little one. Drop in or give us a call."
          color={COLORS.blue}
        />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.3fr' }, gap: 4, alignItems: 'stretch' }}>
          <Box sx={{ display: 'grid', gap: 2.5 }}>
            <Reveal direction="left">
              <ContactCard icon="pin" title="Address" color={COLORS.pink}>{SCHOOL.address}</ContactCard>
            </Reveal>
            <Reveal direction="left" delay={120}>
              <ContactCard icon="phone" title="Admissions Enquiry" color={COLORS.green}>
                <Box component="a" href={`tel:+${SCHOOL.phoneRaw}`} sx={{ color: COLORS.text, fontWeight: 800, textDecoration: 'none', fontSize: 18 }}>
                  {SCHOOL.phone}
                </Box>
                <br />
                Call or WhatsApp
              </ContactCard>
            </Reveal>
            <Reveal direction="left" delay={240}>
              <ContactCard icon="clock" title="Timings" color={COLORS.orange}>{SCHOOL.timings}</ContactCard>
            </Reveal>
          </Box>
          <Reveal direction="right" sx={{ height: '100%' }}>
            <Box sx={{ height: '100%', minHeight: 340, borderRadius: '30px', overflow: 'hidden', border: `6px solid ${COLORS.blue}`, boxShadow: `0 10px 0 ${COLORS.blue}55` }}>
              <Box
                component="iframe"
                title="St Lord's International Public School location"
                src={`https://www.google.com/maps?q=${encodeURIComponent(SCHOOL.mapQuery)}&output=embed`}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                sx={{ border: 0, width: '100%', height: '100%', minHeight: 340, display: 'block' }}
              />
            </Box>
          </Reveal>
        </Box>
      </Container>
    </Section>
  );
}

export default function Footer() {
  return (
    <Box component="footer" sx={{ bgcolor: COLORS.navy, color: '#fff', position: 'relative' }}>
      <Wave color={COLORS.cream} flip sx={{ mt: '-1px' }} />
      <Container maxWidth="lg" sx={{ pt: 4, pb: 3 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.4fr 1fr 1.2fr' }, gap: 5 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.2 }}>
              {IMAGES.logo && (
                <Box
                  component="img"
                  src={IMAGES.logo}
                  alt="St Lord's logo"
                  sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#fff', p: 0.3, border: `2px solid ${COLORS.yellow}`, flexShrink: 0 }}
                />
              )}
              <Box>
                <Typography variant="h4" sx={{ color: COLORS.yellow, fontSize: 30, lineHeight: 1.1 }}>{SCHOOL.name}</Typography>
                <Typography sx={{ fontFamily: FONTS.heading, fontWeight: 700, fontSize: 14, letterSpacing: 1.2 }}>International Public School</Typography>
              </Box>
            </Box>
            <Typography sx={{ opacity: 0.9, lineHeight: 1.7, fontSize: 15 }}>
              {SCHOOL.fullName}, Bangalore. {SCHOOL.tagline}.
            </Typography>
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: COLORS.yellow, mb: 1.5 }}>Quick Links</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.8 }}>
              {NAV.map((n) => (
                <Box key={n.id} component="a" href={`#${n.id}`} sx={{ color: '#fff', opacity: 0.9, textDecoration: 'none', fontSize: 15, fontWeight: 700, '&:hover': { color: COLORS.yellow, opacity: 1 } }}>
                  {n.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: COLORS.yellow, mb: 1.5 }}>Get in Touch</Typography>
            <Typography sx={{ fontSize: 15, lineHeight: 1.7, opacity: 0.92 }}>{SCHOOL.address}</Typography>
            <Typography sx={{ fontSize: 15, mt: 1, opacity: 0.92 }}>Timings: {SCHOOL.timings}</Typography>
            <Button
              href={`https://wa.me/${SCHOOL.phoneRaw}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              startIcon={<Icon name="phone" />}
              sx={{ mt: 2, bgcolor: COLORS.green, px: 3, '&:hover': { bgcolor: '#2fa845' } }}
            >
              {SCHOOL.phone}
            </Button>
          </Box>
        </Box>
        <Box sx={{ mt: 4, pt: 2.5, borderTop: '2px dashed rgba(255,255,255,.25)', textAlign: 'center', fontSize: 14, opacity: 0.85 }}>
          &copy; {new Date().getFullYear()} {SCHOOL.fullName} Day Care. All rights reserved.
        </Box>
      </Container>
    </Box>
  );
}
